import React, {
  createContext,
  useState,
  ReactNode,
  useEffect,
  useCallback,
  useContext,
} from 'react';
import useAuth from '~/hooks/useAuth';
import api from '~/services/api';
import moment from 'moment';
import { useForm } from 'react-hook-form';

import {
  FormFiltros,
  Finalizadora,
  Select,
  FormQuitacao,
  Parcela,
  Conta,
  UseFormProps,
} from './protocols';
import { ReceiptData } from './protocols/receipt.data';
import { schema } from './validations';
import { yupResolver } from '@hookform/resolvers/yup';
import { transformAsCurrency } from '~/utils/functions';
import ImpressaoRecibo from '~/utils/classes/ImpressaoRecibo/ImpressaoRecibo';
import { toast } from 'react-toastify';

moment.locale('pt-br');

type QuitacaoContextData = {
  finalizadoras: Select[];
  onChangeFormFiltros: (form: FormFiltros) => void;
  formFiltros: FormFiltros;
  onChangeFormQuitacao: (form: FormQuitacao) => void;
  formQuitacao: FormQuitacao;
  parcelas: Parcela[];
  setParcelas: React.Dispatch<React.SetStateAction<Parcela[]>>;
  onChangeParcelas: (items: Parcela[]) => void;
  total: number;
  contas: Select[];
  handleInit: (value: boolean) => void;
  init: boolean;
  handleInitPesquisar: (value: boolean) => void;
  initPesquisar: boolean;
  handleInitData: (value: boolean) => void;
  initData: boolean;
  handleClearFormFilters: () => void;
  disabledFields: boolean;
  tipoCategoria: string;
  handleTipoCategoria: (value: string) => void;
  handleDisabledFields: (value: boolean) => void;
  handleCancelFilters: () => void;
  handleClearOptions: () => void;
  clearOptions: boolean;
  receiptData: ReceiptData;
  setReceiptData: React.Dispatch<React.SetStateAction<ReceiptData>>;
  formTela: UseFormProps;
  setLoader: (val: boolean) => void;
  loader: boolean;
  flg_calc_juros: boolean;
  handleJurosParcelas: (items?: Parcela[]) => void;
  showModalRecibo: boolean;
  setShowModalRecibo: React.Dispatch<React.SetStateAction<boolean>>;

  handleQuitarParcelas: () => void;

  loadingTableTitulos: boolean;
  setLoadingTableTitulos: React.Dispatch<React.SetStateAction<boolean>>;

  recibo: string;
  setRecibo: React.Dispatch<React.SetStateAction<string>>;

  focusInput: boolean;
  setFocusInput: React.Dispatch<React.SetStateAction<boolean>>;
};

export const QuitacaoContext = createContext({} as QuitacaoContextData);

interface QuitacaoContextProviderProps {
  children: ReactNode;
}

export function QuitacaoContextProvider({
  children,
}: QuitacaoContextProviderProps): JSX.Element {
  const { user } = useAuth();
  const formFiltrosBlank: FormFiltros = {
    cod_loja: {
      value: user?.loja ? [user?.loja] : [],
      isInvalid: false,
      isRequired: false,
    },
    cod_pessoa: {
      value: { label: '', value: undefined },
      isInvalid: true,
      isRequired: true,
    },
    cod_finalizadora: {
      value: { label: '', value: undefined },
      isInvalid: false,
      isRequired: false,
    },
    cod_categoria_quitacao: {
      value: { label: '', value: undefined },
      isInvalid: false,
      isRequired: false,
    },
    tipo_conta: {
      value: 1,
      isInvalid: false,
      isRequired: false,
    },
    tipo_periodo: {
      value: 1,
      isInvalid: false,
      isRequired: false,
    },
    dta_ini: {
      value: '',
      isInvalid: false,
      isRequired: false,
    },
    dta_fim: {
      value: '',
      isInvalid: false,
      isRequired: false,
    },
  };

  const formQuitacaoBlank: FormQuitacao = {
    cod_finalizadora: {
      value: { label: '', value: undefined },
      isInvalid: false,
      isRequired: false,
    },
    cod_cc: {
      value: { label: '', value: undefined },
      isInvalid: true,
      isRequired: true,
    },
    dta_quitacao: {
      value: '',
      isInvalid: true,
      isRequired: true,
    },
  };
  const {
    register,
    getValues,
    handleSubmit,
    formState,
    control,
    setValue,
    clearErrors,
    watch,
    setError,
    reset,
  } = useForm({ resolver: yupResolver(schema), reValidateMode: 'onBlur' });

  const flg_calc_juros = watch('flg_calc_juros');

  const formTela: UseFormProps = {
    register,
    getValues,
    handleSubmit,
    formState,
    control,
    setValue,
    clearErrors,
    setError,
  };
  const [init, setInit] = useState(false);
  const [initPesquisar, setInitPesquisar] = useState(false);
  const [initData, setInitData] = useState(false);
  const [formFiltros, setFormFiltros] = useState<FormFiltros>(formFiltrosBlank);
  const [formQuitacao, setFormQuitacao] =
    useState<FormQuitacao>(formQuitacaoBlank);
  const [finalizadoras, setFinalizadoras] = useState<Select[]>([]);
  const [parcelas, setParcelas] = useState<Parcela[]>([]);
  const [contas, setContas] = useState<Select[]>([]);
  const [total, setTotal] = useState(0);
  const [disabledFields, setDisabledFields] = useState(false);
  const [tipoCategoria, setTipoCategoria] = useState('Receita');
  const [clearOptions, setClearOptions] = useState(false);
  const [loader, setLoader] = useState<boolean>(false);
  const [receiptData, setReceiptData] = useState<ReceiptData>(
    {} as ReceiptData,
  );
  const [loadingTableTitulos, setLoadingTableTitulos] =
    useState<boolean>(false);
  const [showModalRecibo, setShowModalRecibo] = useState(false);
  const [recibo, setRecibo] = useState('');
  const [focusInput, setFocusInput] = useState<boolean>(true);

  useEffect(() => {
    setValue('dta_quitacao', formQuitacao.dta_quitacao.value);
  }, [formQuitacao.dta_quitacao.value]);

  const onChangeFormFiltros = useCallback((form: FormFiltros) => {
    setFormFiltros(form);
  }, []);

  const onChangeFormQuitacao = useCallback((form: FormQuitacao) => {
    setFormQuitacao(form);
  }, []);

  const getFinalizadoras = useCallback(
    async (lojas: number | number[] | undefined = user?.loja) => {
      const res = await api.get(`/quitacao/finalizadoras/${lojas}`);
      const { data } = res.data;
      const options: Select[] = data.map((item: Finalizadora) => {
        return {
          value: item.cod_finalizadora,
          label: item.des_finalizadora,
        };
      });
      setFinalizadoras(options);
    },
    [],
  );

  const getContas = useCallback(
    async (lojas: number | number[] | undefined = user?.loja) => {
      const res = await api.get(`/quitacao/contas/${lojas}`);
      const { data } = res.data;
      const options: Select[] = data.map((item: Conta) => {
        return {
          label: `${item.cod_banco} - (${item.des_cc})`,
          value: item.cod_cc,
        };
      });
      setContas(options);
    },
    [],
  );

  useEffect(() => {
    const lojas = formFiltros.cod_loja.value;
    if (Array.isArray(lojas) && lojas.length < 1) {
      return setFinalizadoras([]);
    }
    if (!lojas) return setFinalizadoras([]);
    getFinalizadoras(lojas);
    getContas(lojas);
  }, [formFiltros.cod_loja.value, getFinalizadoras, getContas]);

  const onChangeParcelas = async (items: Parcela[]) => {
    const flgCalcJuros = getValues('flg_calc_juros');

    if (flgCalcJuros) {
      handleJurosParcelas(items);
    } else {
      const parcelaWithJuros = await Promise.all(
        items.map(async (parcela) => {
          parcela.val_juros = 0;
          return parcela;
        }),
      );

      setParcelas(parcelaWithJuros);
    }
  };

  const calcSelecionados = () => {
    let sum = 0;
    parcelas
      .filter((i) => i.flg_check)
      .map((parcela) => {
        sum += parcela.val_parcela + parcela.val_juros;
        return parcela;
      });

    setTotal(sum);
  };

  useEffect(() => {
    calcSelecionados();
  }, [parcelas]);

  const handleInit = useCallback((value: boolean): void => {
    setInit(value);
  }, []);

  const handleInitPesquisar = useCallback((value: boolean): void => {
    setInitPesquisar(value);
  }, []);
  const handleInitData = (value: boolean): void => {
    setInitData(value);
  };
  const handleClearFormFilters = useCallback(() => {
    reset();
    const blankForm = {
      ...formFiltrosBlank,
      cod_loja: {
        ...formFiltros.cod_loja,
      },
    };

    setClearOptions(true);
    setFormFiltros(blankForm);
    setFormQuitacao(formQuitacaoBlank);
    setTotal(0);
    setParcelas([]);
    setDisabledFields(false);
    setInit(false);
    setInitPesquisar(false);
    setValue('flg_calc_juros', false);
    setValue('cod_categoria_quitacao', { label: '', value: undefined });
    handleTipoCategoria('Receita');
    setFocusInput(true);
  }, [
    formFiltros.cod_loja,
    formFiltrosBlank,
    formQuitacaoBlank,
    reset,
    setValue,
    setFocusInput,
  ]);

  const handleClearOptions = () => {
    setClearOptions(false);
    setValue('flg_calc_juros', false);
  };

  const handleDisabledFields = useCallback((value: boolean) => {
    setDisabledFields(value);
  }, []);

  const handleTipoCategoria = (value: string) => {
    setTipoCategoria(value);
  };

  const handleCancelFilters = () => {
    setTotal(0);
    setInit(false);
    setFormQuitacao(formQuitacaoBlank);
    setParcelas([]);
    setDisabledFields(false);
    setValue('flg_calc_juros', false);
    setFocusInput(true);
  };

  const handleJurosParcelas = async (items?: Parcela[]) => {
    const flgCalcJuros = getValues('flg_calc_juros');
    const dtaQuitacao = getValues('dta_quitacao');
    const parcelaArray = items || parcelas;
    if (flgCalcJuros) {
      const parcelaWithJuros = await Promise.all(
        parcelaArray.map(async (parcela) => {
          const { data } = await api.post(`/quitacao/val-juros`, {
            cod_loja: parcela.cod_loja,
            val_parcela: transformAsCurrency(parcela.val_parcela),
            dta_vencimento: parcela.dta_vencimento,
            dta_quitada: dtaQuitacao,
          });

          parcela.val_juros = data.data.val_juros;
          return parcela;
        }),
      );
      setParcelas(parcelaWithJuros);
    } else {
      const parcelaWithJuros = await Promise.all(
        parcelaArray.map(async (parcela) => {
          parcela.val_juros = 0;
          return parcela;
        }),
      );

      setParcelas(parcelaWithJuros);
    }

    calcSelecionados();
  };

  const focusErroInput = (inputName: string | null) => {
    if (inputName) {
      const elemento = document.getElementById(inputName);
      if (elemento) {
        elemento.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (inputName === 'dta_quitacao') return;
        elemento.focus();
      }
    }
  };
  const validaInputsParaSubmit = useCallback(
    (inputs: FormQuitacao): boolean => {
      let primeiroInputInvalido: string | null = null;

      for (let i = 0; i < Object.keys(inputs).length; i++) {
        if (
          formQuitacao[Object.keys(inputs)[i] as keyof FormQuitacao]
            .isRequired &&
          formQuitacao[Object.keys(inputs)[i] as keyof FormQuitacao].isInvalid
        ) {
          if (primeiroInputInvalido === null) {
            primeiroInputInvalido = Object.keys(inputs)[i];
          }
          primeiroInputInvalido !== null &&
            focusErroInput(primeiroInputInvalido);
          return false;
        }
      }
      return true;
    },
    [formQuitacao],
  );

  const generateRecibo = useCallback(
    async (data?: ReceiptData, baixar = false) => {
      const selectedParcelas = parcelas.filter((p) => p.flg_check);
      const impressaoRecibo = new ImpressaoRecibo(
        data || receiptData,
        selectedParcelas,
      );
      const reciboGerado = await impressaoRecibo.baixarRecibo(baixar);
      setRecibo(reciboGerado);
      setShowModalRecibo(true);
    },
    [receiptData, parcelas, setRecibo, setShowModalRecibo],
  );

  const handleQuitarParcelas = useCallback(async () => {
    try {
      setLoader(true);
      const parcelasQuitar = parcelas.filter((p) => p.flg_check);
      if (parcelasQuitar.length < 1) {
        return toast.warning('Nenhum título selecionado para quitação');
      }
      
      const parcelasEmPrevisao = parcelasQuitar.filter((p) => p.flg_previsao === true);
      if (parcelasEmPrevisao.length > 0) {
        setLoader(false);
        return toast.warning(
          'Existem títulos em Previsão selecionados. Títulos em Previsão não podem ser quitados! Desmarque a opção se necessário.'
        );
      }
      
      handleInit(true);
      if (!validaInputsParaSubmit(formQuitacao)) {
        return;
      }

      const quitacao = {
        cod_cc: formQuitacao.cod_cc.value.value,
        cod_finalizadora: formFiltros.cod_finalizadora.value.value,
        dta_quitada: formQuitacao.dta_quitacao.value,
        parcelas: parcelasQuitar,
      };
      const res = await api.post('/quitacao', quitacao);

      const { message } = res.data;

      if (formFiltros.tipo_conta.value === 1) {
        setReceiptData(res.data.data[0]);
        generateRecibo(res.data.data[0], false);
      }

      setTimeout(() => {
        setValue('cod_pessoa', { value: undefined, label: '' });
        setFocusInput(true);
      }, 300);

      handleDisabledFields(false);
      handleInitPesquisar(false);
      handleClearFormFilters();

      return toast.success(message);
    } finally {
      setLoader(false);
    }
  }, [
    formFiltros.cod_finalizadora.value.value,
    formFiltros.tipo_conta.value,
    formQuitacao,
    generateRecibo,
    handleClearFormFilters,
    handleDisabledFields,
    handleInit,
    handleInitPesquisar,
    parcelas,
    setLoader,
    setReceiptData,
    validaInputsParaSubmit,
    setValue,
  ]);

  return (
    <QuitacaoContext.Provider
      value={{
        finalizadoras,
        onChangeFormFiltros,
        formFiltros,
        onChangeFormQuitacao,
        formQuitacao,
        onChangeParcelas,
        parcelas,
        init,
        handleInit,
        initPesquisar,
        handleInitPesquisar,
        handleInitData,
        initData,
        total,
        contas,
        handleClearFormFilters,
        disabledFields,
        handleDisabledFields,
        handleCancelFilters,
        tipoCategoria,
        handleTipoCategoria,
        handleClearOptions,
        clearOptions,
        receiptData,
        setReceiptData,
        formTela,
        setLoader,
        loader,
        flg_calc_juros,
        handleJurosParcelas,
        loadingTableTitulos,
        setLoadingTableTitulos,
        showModalRecibo,
        setShowModalRecibo,

        handleQuitarParcelas,

        recibo,
        setRecibo,
        setParcelas,
        focusInput,
        setFocusInput,
      }}
    >
      {children}
    </QuitacaoContext.Provider>
  );
}

export const useQuitacao = (): QuitacaoContextData => {
  return useContext(QuitacaoContext);
};
